import { Button } from '@factorialco/deprecated-design-system'
import { some } from 'lodash'

import { observer } from 'mobx-react'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import { Options } from 'modules/expenses/components/expenses/DataTable/_Actions/Options'
import type { Expensable } from 'modules/expenses/components/expenses/DataTable/types'
import { ActionFn } from 'modules/expenses/components/expenses/DataTable/useActions'
import {
  canAddExpensableToGroup,
  canAddExpensableToPayment,
  canAddExpensableToPayroll,
  canRemoveExpensableFromGroup,
  canRemoveExpensableFromPayroll,
  canReviewExpensable,
} from 'modules/expenses/lib/expensables/availableActions'
import {
  everyReimbursableCurrencyAllowed,
  showNotAllowedCurrenciesMessage,
} from 'modules/expenses/lib/expensables/expensable'
import { useMyExpenses } from 'modules/expenses/pages/my_expenses/MyExpensesContext'

interface ExpensableActionsProps {
  expensable: Expensable
  onApprove: ActionFn
  onReject: ActionFn
  onRestartReview: ActionFn
  onAddToPayroll: ActionFn
  onAddToPayment: ActionFn
  onRemoveFromPayroll: ActionFn
  onSetToPaid: ActionFn
  onRevertPaid: ActionFn
  onAddToGroup: ActionFn
  onRemoveFromGroup: ActionFn
}

export const Actions = observer(
  ({
    expensable,
    onApprove,
    onReject,
    onRestartReview,
    onAddToPayroll,
    onAddToPayment,
    onRemoveFromPayroll,
    onSetToPaid,
    onAddToGroup,
    onRemoveFromGroup,
  }: ExpensableActionsProps) => {
    const { employee } = useCurrentSession()
    const { featureAvailabilities } = useMyExpenses()
    const withExpensable = (action: ActionFn) => () => action([expensable])

    const availableActions = {
      canReview: [expensable].some((e) => canReviewExpensable(e, 'personal')),
      canAddToGroup: [expensable].some((e) =>
        canAddExpensableToGroup(featureAvailabilities.expensableGroups, e, 'personal', employee)
      ),
      canRemoveFromGroup: [expensable].some((e) => canRemoveExpensableFromGroup(e, 'personal')),
      canRemoveFromPayroll: [expensable].some((e) =>
        canRemoveExpensableFromPayroll(e, featureAvailabilities.reimburseThroughPayroll)
      ),
      canAddToPayroll: [expensable].some((e) =>
        canAddExpensableToPayroll(e, featureAvailabilities.reimburseThroughPayroll)
      ),
      canAddToPayment: [expensable].some((e) =>
        canAddExpensableToPayment(e, featureAvailabilities.reimburseThroughSepa)
      ),
    }

    if (!some(availableActions)) return null

    return (
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary" iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start">
          <Options
            {...availableActions}
            onApprove={withExpensable(onApprove)}
            onReject={withExpensable(onReject)}
            onRestartReview={withExpensable(onRestartReview)}
            onSetToPaid={withExpensable(onSetToPaid)}
            onRemoveFromPayroll={withExpensable(onRemoveFromPayroll)}
            onAddToGroup={withExpensable(onAddToGroup)}
            onRemoveFromGroup={withExpensable(onRemoveFromGroup)}
            onAddToPayroll={() => {
              if (!everyReimbursableCurrencyAllowed([expensable])) {
                return showNotAllowedCurrenciesMessage()
              }

              withExpensable(onAddToPayroll)()
            }}
            onAddToPayment={withExpensable(onAddToPayment)}
          />
        </PopoverMenu.Content>
      </PopoverMenu>
    )
  }
)
